import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Card, CardBody, CardHeader } from "../../components/card/card";
import { meter, meterReading } from "../../api";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import LoadingIndicator from "../../components/LoadingIndicator";
import { format } from "date-fns";
import { Badge } from "react-bootstrap";
import DataTable from "react-data-table-component";
import ConfirmationDlg from "../../components/ConfirmationDlg";
import NormalInput from "../../components/form/HorizontalNormalInput";
import NormalTextArea from "../../components/form/HorizontalNormalTextarea";
import { formatDateOnly } from "../../utils/helper";
import { PermissionChecker } from "../../components/PermissionChecker";

function MeterReadings() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meterData, setMeterData] = useState(null);
  const [query, setQuery] = useState({ page: 1,take:10,totalData:1 });
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddReading, setShowAddReading] = useState(false);
  const [deleteReading, setDeleteReading] = useState(null);
  const { config } = useAuth();

  const [newReading, setNewReading] = useState({
    reading_value: "",
    reading_date: new Date().toISOString().split('T')[0],
    notes: "",
  });

  async function getMeterData() {
    try {
      setLoading(true);
      const res = await meter.getOne(id);
      if (res.ok) {
        const body = await res.json();
        setMeterData(body.data);
      } else {
        toast.error("Failed to load meter data");
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error("Failed to load meter data");
      console.error(error);
    }
  }

  async function getReadings() {
    try {
      const res = await meterReading.list({ meter_id: id, ...query  });
      if (res.ok) {
        const body = await res.json();
        setReadings(body.data.results || []);
        if (body.data.totalData) {
          setQuery((prev) => ({
            ...prev,
            totalData:body.data.totalData
          }))
        }
      }
    } catch (error) {
      console.error("Failed to load readings:", error);
    }
  }

  useEffect(() => {
    if (id) {
      getMeterData();
    }
  }, [id]);

  useEffect(() => {
    if(id){
      getReadings();
    }
  }, [id,query.page, query.take]);

  const handleAddReading = async () => {
    try {
      if (!newReading.reading_value) {
        toast.error("Reading value is required");
        return;
      }

      const res = await meterReading.create({
        ...newReading,
        meter_id: id,
      });

      if (res.ok) {
        toast.success("Reading added successfully");
        setNewReading({
          reading_value: "",
          reading_date: new Date().toISOString().split('T')[0],
          notes: "",
        });
        setShowAddReading(false);
        getReadings();
      } else {
        toast.error("Failed to add reading");
      }
    } catch (error) {
      toast.error("Failed to add reading");
      console.error(error);
    }
  };

  const handleDeleteReading = async () => {
    if (!deleteReading) return;
    try {
      const res = await meterReading.delete(deleteReading.id);
      if (res.ok) {
        toast.success("Reading deleted successfully");
        getReadings();
      } else {
        toast.error("Failed to delete reading");
      }
    } catch (error) {
      toast.error("Failed to delete reading");
      console.error(error);
    }
    setDeleteReading(null);
  };

  const columns = [
    {
      name: "Date",
      selector: (row) => row.reading_date,
      cell: (row) => (
        <div>
          {formatDateOnly(row.reading_date)}
        </div>
      ),
     
    },
    {
      name: "Reading",
      selector: (row) => row.reading_value,
      cell: (row) => (
        <div className="fw-bold">
          {row.reading_value} {meterData?.unit?.name}
        </div>
      ),
 
    },  
    {
      name: "Notes",
      selector: (row) => row.notes,
      cell: (row) => (
        <div style={{ maxWidth: "200px" }}>
          {row.notes || "N/A"}
        </div>
      ),
    },
    {
      name: "Recorded By",
      selector: (row) => row.recorded_by,
      cell: (row) => (
        <div>
          {row.recorded_by_user?.firstname + " " + row.recorded_by_user?.lastname || "System"}
        </div>
      ),
    },

    {
      name: "Actions",
      cell: (row) => (
        <div>
          <PermissionChecker permissions={["delete meter reading"]} hideIfNotAllowed>
            <button className="btn btn-sm btn-outline-danger" onClick={() => setDeleteReading(row)}>Delete</button>
          </PermissionChecker>
        </div>
      ),
    },
   
  ];

  

 
  return (
    <div>
      <ConfirmationDlg
        size={"lg"}
        open={!!deleteReading}
        onClose={() => setDeleteReading(null)}
        btnSubmitText={"Delete"}
        btnCancelText={"Cancel"}
        onSubmit={handleDeleteReading}
        title="Delete Reading"
      >
        <div className="text-center">
          <p>Are you sure you want to delete this reading?</p>
          <p className="text-muted">
            {deleteReading?.reading_value} {meterData?.unit?.name} on {formatDateOnly(deleteReading?.reading_date)}
          </p>
        </div>
      </ConfirmationDlg>

      <ConfirmationDlg
        size={"lg"}
        open={showAddReading}
        onClose={() => setShowAddReading(false)}
        btnSubmitText={"Save Reading"}
        btnCancelText={"Cancel"}
        onSubmit={handleAddReading}
        title="Add New Reading"
      >
        <div className="row">
          <div className="col-md-6">
            <NormalInput
              required={true}
              value={newReading.reading_value}
              label="Reading Value"
              onchange={(e) => setNewReading({ ...newReading, reading_value: e.target.value })}
              name="reading_value"
              type="number"
              placeholder={`Enter reading in ${meterData?.unit?.name}`}
            />
          </div>
          <div className="col-md-6">
            <NormalInput
              required={true}
              value={newReading.reading_date}
              label="Reading Date"
              onchange={(e) => setNewReading({ ...newReading, reading_date: e.target.value })}
              name="reading_date"
              type="date"
            />
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12">
            <NormalTextArea
              value={newReading.notes}
              label="Notes"
              onchange={(e) => setNewReading({ ...newReading, notes: e.target.value })}
              name="notes"
              rows={3}
              placeholder="Optional notes about this reading"
            />
          </div>
        </div>
      </ConfirmationDlg>

      <div className="d-flex align-items-center mb-3">
       

        <div className="ms-auto">
        
          <PermissionChecker permissions={["create meter reading"]} hideIfNotAllowed>
          <button
            onClick={() => setShowAddReading(true)}
            className="btn btn-theme"
          >
            <i className="fa fa-plus fa-fw me-1"></i> Add Reading
            </button>
            </PermissionChecker>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="row">
            <div className="col-md-6">
              <h3 className="card-title">Meter Information</h3>
              <p className="text-muted mb-0">
                {meterData?.meter_name}
              </p>
            </div>
           
          </div>
        </CardHeader>
        <CardBody>
          <DataTable
            progressPending={loading}
            pointerOnHover
            highlightOnHover
            theme="dark"
            columns={columns}
            data={readings}
            pagination
            paginationServer
            onChangeRowsPerPage={(row)=>setQuery((prev)=>({...prev,page:1,take:row}))}
            paginationTotalRows={query.totalData}
            onChangePage={(page)=>setQuery((prev)=>({...prev,page:page}))}
           
          />
        </CardBody>
      </Card>
    </div>
  );
}

export default MeterReadings; 